/* eslint-disable @reatom/unit-naming-rule */
import { action, atom } from '@reatom/framework'
import * as DateF from 'date-fns'

import { Order } from '@blade/std-lib'
import { CurrencyAddress } from '@root/entities/currency'
import { LimitOrderId } from '@root/entities/limit-order/model/limit-order-id'
import { Network } from '@root/entities/network/model'
import { PairExtraDetails } from '@root/entities/pair/model'
import { Provider } from '@root/entities/provider'
import { TradeSettings } from '@root/entities/trade-settings/model'
import { TxHash } from '@root/entities/transaction'
import { walletsByIdAtom } from '@root/entities/wallet'
import { WalletAddress, WalletId } from '@root/entities/wallet/model'
import { reatomBigNumber } from '@root/shared/lib'

import { SwapDetails } from '../model'

import type { TradeSwap } from '@blade/blockchain-types'
import type { Ctx } from '@reatom/framework'
import type { PairModel } from '@root/entities/pair/atoms'
import type { TradeApi } from '@root/shared/api'

export type Status = 'active' | 'failed' | 'finished'

export const reatomSwapOrder = ({
  dto,
  pair,
}: {
  dto: TradeSwap | TradeApi.MarketOrderDto
  pair: PairModel
}) => {
  //#region Static
  const id = LimitOrderId(dto.id)
  const type = dto.type
  const createdAt = new Date(dto.createdAt)
  const walletId = WalletId(dto.walletId)
  const walletAddress = WalletAddress(dto.walletAddress)
  const network = Network.fromDto(dto.network)
  const currencyInAddress = CurrencyAddress(dto.tokenIn)
  const currencyOutAddress = CurrencyAddress(dto.tokenOut)
  const currencyIn =
    pair.quote.address === currencyInAddress ? pair.quote : pair.base
  const currencyOut =
    pair.base.address === currencyOutAddress ? pair.base : pair.quote

  // TODO: Support swap orders without pair meta
  const provider = (() => {
    if (!dto.provider) {
      // TODO: Remove hardcoded provider
      return Provider.pumpFun
    }

    return Provider.fromDto(dto.provider, {
      isPumpFunMayhem:
        dto.pairMeta && dto.pairMeta.extraDetails
          ? PairExtraDetails.isPumpFunMayhem(dto.pairMeta.extraDetails)
          : false,
    })
  })()
  const settings = TradeSettings.fromDto(dto.settings, network)
  //#endregion

  //#region State
  const updatedAtAtom = atom(new Date(dto.updatedAt))
  const statusAtom = atom(dto.status)
  const swapDetailsAtom = atom(
    dto.swapDetails ? SwapDetails.fromDto(dto.swapDetails) : undefined,
  )
  const txHashAtom = atom(TxHash(dto.txHash))
  const averageExecutionBuyPriceAtom = reatomBigNumber(
    dto.rollingStats.averageBuyPrice,
  )
  const averageExecutionBuyMarketCapAtom = reatomBigNumber(
    dto.rollingStats.averageBuyMarketCap,
  )
  //#endregion

  //#region Computed
  const walletAtom = atom((ctx) => {
    const wallets = ctx.spy(walletsByIdAtom)
    return wallets.get(walletId)
  })
  //#endregion

  const updateFromDtoAction = action((ctx, update: TradeSwap) => {
    const currentUpdatedAt = ctx.get(updatedAtAtom)
    const updatedAt = new Date(update.updatedAt)
    if (DateF.isAfter(currentUpdatedAt, updatedAt)) {
      return
    }
    updatedAtAtom(ctx, updatedAt)
    statusAtom(ctx, update.status)
    swapDetailsAtom(
      ctx,
      update.swapDetails ? SwapDetails.fromDto(update.swapDetails) : undefined,
    )
    txHashAtom(ctx, TxHash(update.txHash))
    averageExecutionBuyPriceAtom.set(ctx, update.rollingStats.averageBuyPrice)
    averageExecutionBuyMarketCapAtom.set(
      ctx,
      update.rollingStats.averageBuyMarketCap,
    )
  })

  return {
    id,
    type,
    pair,
    createdAt,
    walletId,
    walletAddress,
    network,
    currencyIn,
    currencyOut,
    currencyInAddress,
    currencyOutAddress,
    provider,
    settings,

    txHash: txHashAtom,
    updatedAt: updatedAtAtom,
    status: statusAtom,
    swapDetails: swapDetailsAtom,
    averageExecutionBuyPrice: averageExecutionBuyPriceAtom,
    averageExecutionBuyMarketCap: averageExecutionBuyMarketCapAtom,

    wallet: walletAtom,

    updateFromDto: updateFromDtoAction,
  }
}

export type SwapOrderModel = ReturnType<typeof reatomSwapOrder>

reatomSwapOrder.cmpByUpdatedAt =
  (ctx: Ctx): Order.Order<SwapOrderModel> =>
  (left, right) => {
    const updatedAtLeft = ctx.spy
      ? ctx.spy(left.updatedAt)
      : ctx.get(left.updatedAt)
    const updatedAtRight = ctx.spy
      ? ctx.spy(right.updatedAt)
      : ctx.get(right.updatedAt)
    return Order.Date(updatedAtLeft, updatedAtRight)
  }
reatomSwapOrder.cmpByUpdatedAtDesc = (ctx: Ctx): Order.Order<SwapOrderModel> =>
  Order.flip(reatomSwapOrder.cmpByUpdatedAt(ctx))
